import type { EntityState, PickUpRefusalReason } from "odyssey-shared";
import { gameReport } from "../report.ts";
import { groundUnder, nearestGround } from "./find.ts";
import { pickUpRefusalLine } from "./words.ts";

/**
 * Picking up ground items from the page (P2.8-T65, ADR-091's amendment for P2.8-T13, decision 3): the interact key
 * picks up the nearest ground item within the map server's reach of the predicted feet point when no thing is the
 * interaction's target, and a left click on an item's drawn square picks that one up, sending `pickUp` either way. The
 * server decides every pick-up against its own items, view, reach and bag; the page sends only the handle. A refusal
 * is shown as a refused use is, the refusal sound with no line, except `owned` and `full`, which say their line
 * (`words.ts`), `owned` with the server's time until the item turns public; the page never works ownership out. A pick-up that commits answers with `bagChanged` and `lootGained`, whose items the loot toast shows.
 */

/**
 * How many sent handles and refusals the report keeps, the newest last.
 */
const REPORT_KEPT = 32;

/**
 * Lists a value at the end of a report's list, dropping the oldest past `REPORT_KEPT`.
 *
 * @param list The list.
 * @param value The value.
 */
function keep<T>(list: T[], value: T): void {
  list.push(value);
  if (list.length > REPORT_KEPT) {
    list.shift();
  }
}

/**
 * What the pick-up reads of the session, each asked as it acts.
 */
export interface PickUpHost {
  /** The client's view, null outside a connection. */
  readonly entities: () => ReadonlyMap<number, EntityState> | null;
  /** The predicted local character's feet point in sixteenths, undefined while there is none. */
  readonly feet: () => { readonly x: number; readonly y: number } | undefined;
  /**
   * Sends one `pickUp`.
   *
   * @param handle The item's handle.
   * @returns Whether it went: false before the world is shown, while a Game Master's freeze refuses acts, or when the
   *   connection did not send it.
   */
  readonly send: (handle: number) => boolean;
  /** Half an item's drawn square in sixteenths. */
  readonly half: () => number;
  /** Plays the refusal sound. */
  readonly refused: () => void;
  /**
   * Says a line on the message line.
   *
   * @param line The line.
   */
  readonly say: (line: string) => void;
}

/**
 * The page's pick-up.
 */
export class GroundPickUp {
  /**
   * @param host What the pick-up reads of the session.
   */
  public constructor(private readonly host: PickUpHost) {}

  /**
   * Whether a ground item is in reach, asked without acting: what the interact button's label follows.
   *
   * @returns True when `nearest` would find an item.
   */
  public inReach(): boolean {
    const entities = this.host.entities();
    const feet = this.host.feet();
    return entities !== null && feet !== undefined && nearestGround(entities, feet.x, feet.y) >= 0;
  }

  /**
   * The interact key's pick-up, asked when no thing is its target: the nearest ground item in reach.
   *
   * @returns Whether an item was in reach, so the key has acted whether or not the request went.
   */
  public nearest(): boolean {
    const entities = this.host.entities();
    const feet = this.host.feet();
    if (entities === null || feet === undefined) {
      return false;
    }
    const handle = nearestGround(entities, feet.x, feet.y);
    if (handle < 0) {
      return false;
    }
    this.send(handle);
    return true;
  }

  /**
   * A left click's pick-up: the item whose drawn square holds the point, which the server checks for reach.
   *
   * @param x The point's x in sixteenths.
   * @param y The point's y in sixteenths.
   * @returns Whether an item was under the point, so the click is taken and selects and swings at nothing.
   */
  public at(x: number, y: number): boolean {
    const entities = this.host.entities();
    if (entities === null) {
      return false;
    }
    const handle = groundUnder(entities, x, y, this.host.half());
    if (handle < 0) {
      return false;
    }
    this.send(handle);
    return true;
  }

  /**
   * Shows a refused pick-up: the refusal sound, and the line of a reason that has one.
   *
   * @param reason The server's reason.
   * @param waitMs For `owned`, the server's time until the item turns public.
   */
  public refused(reason: PickUpRefusalReason, waitMs: number): void {
    keep(gameReport.pickUp.refused, reason);
    this.host.refused();
    const line = pickUpRefusalLine(reason, waitMs);
    if (line !== null) {
      this.host.say(line);
    }
  }

  /**
   * Asks the map server to pick up an item by its handle, as Auto loot does (P2.8-T79).
   *
   * @param handle The item's handle.
   */
  public ask(handle: number): void {
    this.send(handle);
  }

  /**
   * Sends one `pickUp` and lists it for the report when it went.
   *
   * @param handle The item's handle.
   */
  private send(handle: number): void {
    if (this.host.send(handle)) {
      keep(gameReport.pickUp.sent, handle);
    }
  }
}
